import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { api } from '../../../config/api';

// Validation schemas
const addressSchema = z.object({
  line1: z.string().min(1, 'Address line 1 is required'),
  line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State/Province is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
});

/**
 * Phone validation - Industry Best Practices (E.164 compatible)
 * Accepts: +1234567890, (123) 456-7890, 123-456-7890, +44 20 7123 4567, etc.
 * Min length: 7 digits (smallest valid phone numbers)
 * Max length: 50 characters (including formatting)
 */
const phoneValidator = z.string()
  .trim()
  .min(7, 'Phone number must be at least 7 characters')
  .max(50, 'Phone number too long')
  .regex(/^[\d\s\-\(\)\+\.ext]+$/i, 'Phone number contains invalid characters');

const contactSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  title: z.string().trim().optional().or(z.literal('')),
  email: z.string().trim().email('Invalid email'),
  phone: phoneValidator,
});

// Secondary contact schema - all fields optional
// Use union type to allow empty strings OR valid data
const secondaryContactSchema = z.union([
  z.object({
    name: z.string().trim().min(1).max(255),
    title: z.string().trim().max(100).optional().or(z.literal('')),
    email: z.string().trim().email('Invalid email').max(255),
    phone: phoneValidator,
  }),
  z.object({
    name: z.literal('').optional(),
    title: z.literal('').optional(),
    email: z.literal('').optional(),
    phone: z.literal('').optional(),
  }),
]).optional();

const bankInfoSchema = z.object({
  bankName: z.string().min(1, 'Bank name is required'),
  accountNumber: z.string()
    .min(1, 'Account number is required'),
  accountHolderName: z.string().min(1, 'Account holder name is required'),
  routingNumber: z.string().optional(),
  swiftCode: z.string().optional(),
  iban: z.string().optional(),
  bankAddress: z.string().optional(),
});

const customerRegistrationSchema = z.object({
  entityName: z.string().trim().min(1, 'Company name is required').max(255),
  email: z.string().trim().email('Invalid email').max(255),
  phone: phoneValidator,
  mobile: phoneValidator.optional().or(z.literal('')),
  fax: phoneValidator.optional().or(z.literal('')),
  website: z.string()
    .trim()
    .transform(val => {
      if (!val) return '';
      if (!/^https?:\/\//i.test(val)) return `https://${val}`;
      return val;
    })
    .pipe(z.string().url('Invalid URL').max(255).optional().or(z.literal(''))),
  businessAddress: addressSchema,
  remittanceAddress: addressSchema,
  primaryContact: contactSchema,
  secondaryContact: secondaryContactSchema,
  /**
   * Tax ID / EIN Validation - Flexible for International Use
   * Supports formats from different countries:
   * - US EIN: 12-3456789 or 123456789 (9 digits)
   * - Canada BN: 123456789 (9 digits)
   * - UK VAT: GB123456789
   * - EU VAT: Various formats
   */
  taxId: z.string().trim().min(1, 'Tax ID/EIN/VAT is required'),
  dunsNumber: z.string()
    .trim()
    .regex(/^[\d\-\s]*$/, 'DUNS number can only contain digits, hyphens, and spaces')
    .max(50)
    .optional()
    .or(z.literal('')),
  creditLimit: z.number().positive().optional(),
  paymentTerms: z.string().min(1, 'Payment terms are required'),
  currency: z.string().default('USD'),
  bankInfo: bankInfoSchema,
  businessType: z.string().min(1, 'Business type is required'),
  yearsInBusiness: z.number().int().min(0).optional(),
  numberOfEmployees: z.number().int().min(1).optional(),
  annualRevenue: z.number().positive().optional(),
  certifications: z.array(z.string()).optional(),
  insuranceDetails: z.string().optional(),
  w9FormUrl: z.string().optional(),
  certificateOfInsuranceUrl: z.string().optional(),
  bankLetterUrl: z.string().optional(),
  businessLicenseUrl: z.string().optional(),
  notes: z.string().optional(),
  specialRequirements: z.string().optional(),
});

type CustomerRegistrationForm = z.infer<typeof customerRegistrationSchema>;
const CUSTOMER_TOTAL_STEPS = 5;

// State options
const usStates = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const CustomerRegistrationPageModern: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<CustomerRegistrationForm>({
    resolver: zodResolver(customerRegistrationSchema),
    mode: 'onChange',
    defaultValues: {
      currency: 'USD',
    },
  });

  const businessAddress = watch('businessAddress');
  const businessCountry = watch('businessAddress.country');
  const remittanceCountry = watch('remittanceAddress.country');

  // Fetch countries from REST Countries API
  useEffect(() => {
    fetch('https://restcountries.com/v3.1/all')
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const countryNames = data
            .map((c: any) => c.name.common)
            .sort((a, b) => a.localeCompare(b));
          console.log(`Loaded ${countryNames.length} countries from REST Countries API`);
          setCountries(countryNames);
        } else {
          throw new Error('Invalid data format from REST Countries API');
        }
      })
      .catch(err => {
        console.error('Failed to load countries from REST Countries API:', err);
        // Comprehensive fallback list
        const fallbackCountries = [
          'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
          'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
          'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
          'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
          'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
          'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
          'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia',
          'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
          'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India',
          'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
          'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
          'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macedonia',
          'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
          'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
          'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
          'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea',
          'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
          'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
          'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
          'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan', 'Spain', 'Sri Lanka',
          'Sudan', 'Suriname', 'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
          'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
          'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
          'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
        ];
        console.log(`Using fallback list with ${fallbackCountries.length} countries`);
        setCountries(fallbackCountries);
      });
  }, []);

  React.useEffect(() => {
    if (sameAsBusinessAddress && businessAddress) {
      setValue('remittanceAddress', businessAddress);
    }
  }, [sameAsBusinessAddress, businessAddress, setValue]);

  const onSubmit = async (rawData: CustomerRegistrationForm) => {
    if (currentStep !== CUSTOMER_TOTAL_STEPS) {
      return;
    }

    // If same address checkbox is checked, copy business address to remittance
    const data = sameAsBusinessAddress
      ? { ...rawData, remittanceAddress: rawData.businessAddress }
      : rawData;

    console.log('Form submitted with data:', data);
    setIsSubmitting(true);
    try {
      console.log('Sending registration request to:', '/customers/register');
      const response = await api.post('/customers/register', data);
      console.log('Registration response:', response.data);
      alert(`Registration successful! Your registration number is: ${response.data.data.registrationNumber}`);
      navigate('/registration-success', {
        state: { registrationNumber: response.data.data.registrationNumber },
      });
    } catch (error: any) {
      console.error('Registration submission failed:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error?.message ||
                          error.message ||
                          'Unknown error occurred';
      alert(`Registration failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, name: 'Company Info', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { id: 2, name: 'Addresses', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
    { id: 3, name: 'Contacts', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { id: 4, name: 'Financial', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 5, name: 'Business', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ];

  const getStepProgress = () => (currentStep / steps.length) * 100;

  // Get fields for each step for validation
  const getStepFields = (step: number): (keyof CustomerRegistrationForm | string)[] => {
    switch (step) {
      case 1:
        return ['entityName', 'email', 'phone', 'mobile', 'fax', 'website'];
      case 2:
        return [
          'businessAddress.line1', 'businessAddress.city', 'businessAddress.state',
          'businessAddress.postalCode', 'businessAddress.country',
          'remittanceAddress.line1', 'remittanceAddress.city', 'remittanceAddress.state',
          'remittanceAddress.postalCode', 'remittanceAddress.country'
        ];
      case 3:
        return [
          'primaryContact.name', 'primaryContact.email', 'primaryContact.phone',
          'secondaryContact.name', 'secondaryContact.email', 'secondaryContact.phone'
        ];
      case 4:
        return [
          'taxId', 'paymentTerms',
          'bankInfo.bankName', 'bankInfo.accountNumber', 'bankInfo.accountHolderName'
        ];
      case 5:
        return ['businessType', 'yearsInBusiness', 'notes', 'specialRequirements'];
      default:
        return [];
    }
  };

  // Find first step with errors
  const findFirstStepWithErrors = (): number => {
    for (let step = 1; step <= steps.length; step++) {
      const fields = getStepFields(step);
      for (const field of fields) {
        const fieldPath = field.toString().split('.');
        let errorObj: any = errors;
        for (const path of fieldPath) {
          errorObj = errorObj?.[path];
          if (!errorObj) break;
        }
        if (errorObj && errorObj.message) {
          return step;
        }
      }
    }
    return currentStep;
  };

  // Validate current step before proceeding
  const handleNextStep = async () => {
    const fields = getStepFields(currentStep) as any[];
    const isValid = await trigger(fields);

    if (isValid) {
      setCurrentStep(Math.min(CUSTOMER_TOTAL_STEPS, currentStep + 1));
    } else {
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFinalSubmit = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 animate-fadeIn">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-primary shadow-glow-lg mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-4xl font-display font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
              Customer Registration
            </h1>
            <p className="text-gray-600 text-lg">Join Guston Portal as a valued customer</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8 animate-slideInRight">
            <div className="relative">
              <div className="overflow-hidden h-3 mb-6 text-xs flex rounded-full bg-gray-200">
                <div
                  style={{ width: `${getStepProgress()}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-primary transition-all duration-500"
                ></div>
              </div>
            </div>

            {/* Steps */}
            <div className="flex justify-between">
              {steps.map((step) => (
                <div key={step.id} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center font-semibold transition-all duration-300 ${
                      currentStep >= step.id
                        ? 'bg-gradient-primary text-white shadow-glow-lg'
                        : 'bg-white text-gray-400 border-2 border-gray-200'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={step.icon} />
                    </svg>
                  </div>
                  <p className={`text-xs mt-2 font-medium ${currentStep >= step.id ? 'text-primary-600' : 'text-gray-500'}`}>
                    {step.name}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-card-hover p-8 sm:p-10 border border-gray-100 animate-slideInRight">
            <form onSubmit={(e) => e.preventDefault()} onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
                if (currentStep < CUSTOMER_TOTAL_STEPS) {
                  handleNextStep();
                }
              }
            }}>
              {/* Error Summary Banner */}
              {Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                  <h3 className="text-red-800 font-semibold mb-2 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Please fix the following errors:
                  </h3>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {Object.entries(errors).map(([field, error]: [string, any]) => {
                      // Handle nested errors
                      if (error && typeof error === 'object' && !error.message) {
                        return Object.entries(error).map(([nestedField, nestedError]: [string, any]) => {
                          if (nestedError && typeof nestedError === 'object' && !nestedError.message) {
                            return Object.entries(nestedError).map(([deepField, deepError]: [string, any]) => (
                              <li key={`${field}.${nestedField}.${deepField}`}>
                                {field}.{nestedField}.{deepField}: {deepError?.message}
                              </li>
                            ));
                          }
                          return (
                            <li key={`${field}.${nestedField}`}>
                              {field}.{nestedField}: {nestedError?.message}
                            </li>
                          );
                        });
                      }
                      return error?.message ? (
                        <li key={field}>{field}: {error.message}</li>
                      ) : null;
                    })}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      const firstErrorStep = findFirstStepWithErrors();
                      setCurrentStep(firstErrorStep);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="mt-3 text-sm text-red-700 hover:text-red-900 underline font-medium"
                  >
                    Jump to first error
                  </button>
                </div>
              )}

              {/* Step 1: Company Information */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Company Information</h2>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      {...register('entityName')}
                      className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      placeholder="Enter your company name"
                    />
                    {errors.entityName && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.entityName.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('email')}
                        type="email"
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="company@example.com"
                      />
                      {errors.email && <p className="mt-2 text-sm text-error-600">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('phone')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="+1 (555) 123-4567"
                      />
                      {errors.phone && <p className="mt-2 text-sm text-error-600">{errors.phone.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile</label>
                      <input
                        {...register('mobile')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="+1 (555) 987-6543"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Fax</label>
                      <input
                        {...register('fax')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="+1 (555) 123-4568"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                      <input
                        {...register('website')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Addresses */}
              {currentStep === 2 && (
                <div className="space-y-8 animate-fadeIn">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Business Address</h2>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Address Line 1 <span className="text-error-500">*</span>
                        </label>
                        <input
                          {...register('businessAddress.line1')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          placeholder="123 Main Street"
                        />
                        {errors.businessAddress?.line1 && (
                          <p className="mt-2 text-sm text-error-600">{errors.businessAddress.line1.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Address Line 2</label>
                        <input
                          {...register('businessAddress.line2')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          placeholder="Suite 100"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            City <span className="text-error-500">*</span>
                          </label>
                          <input
                            {...register('businessAddress.city')}
                            className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          />
                          {errors.businessAddress?.city && (
                            <p className="mt-2 text-sm text-error-600">{errors.businessAddress.city.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            State/Province <span className="text-error-500">*</span>
                          </label>
                          {businessCountry === 'United States' ? (
                            <select
                              {...register('businessAddress.state')}
                              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                            >
                              <option value="">Select a state</option>
                              {usStates.map((state) => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              {...register('businessAddress.state')}
                              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                              placeholder="Enter state/province"
                            />
                          )}
                          {errors.businessAddress?.state && (
                            <p className="mt-2 text-sm text-error-600">{errors.businessAddress.state.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Postal Code <span className="text-error-500">*</span>
                          </label>
                          <input
                            {...register('businessAddress.postalCode')}
                            className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          />
                          {errors.businessAddress?.postalCode && (
                            <p className="mt-2 text-sm text-error-600">{errors.businessAddress.postalCode.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Country <span className="text-error-500">*</span>
                          </label>
                          <select
                            {...register('businessAddress.country')}
                            className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          >
                            <option value="">Select a country</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                          {errors.businessAddress?.country && (
                            <p className="mt-2 text-sm text-error-600">{errors.businessAddress.country.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-display font-bold text-gray-900">Remittance Address</h2>
                      <label className="flex items-center cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={sameAsBusinessAddress}
                          onChange={(e) => setSameAsBusinessAddress(e.target.checked)}
                          className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500 cursor-pointer"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-primary-600 transition-colors">
                          Same as business address
                        </span>
                      </label>
                    </div>

                    {!sameAsBusinessAddress && (
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Address Line 1 <span className="text-error-500">*</span>
                          </label>
                          <input
                            {...register('remittanceAddress.line1')}
                            className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Address Line 2</label>
                          <input
                            {...register('remittanceAddress.line2')}
                            className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              City <span className="text-error-500">*</span>
                            </label>
                            <input
                              {...register('remittanceAddress.city')}
                              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              State/Province <span className="text-error-500">*</span>
                            </label>
                            {remittanceCountry === 'United States' ? (
                              <select
                                {...register('remittanceAddress.state')}
                                className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                              >
                                <option value="">Select a state</option>
                                {usStates.map((state) => (
                                  <option key={state} value={state}>{state}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                {...register('remittanceAddress.state')}
                                className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                                placeholder="Enter state/province"
                              />
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Postal Code <span className="text-error-500">*</span>
                            </label>
                            <input
                              {...register('remittanceAddress.postalCode')}
                              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Country <span className="text-error-500">*</span>
                            </label>
                            <select
                              {...register('remittanceAddress.country')}
                              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                            >
                              <option value="">Select a country</option>
                              {countries.map((country) => (
                                <option key={country} value={country}>{country}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Contacts */}
              {currentStep === 3 && (
                <div className="space-y-8 animate-fadeIn">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Primary Contact</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Name <span className="text-error-500">*</span>
                        </label>
                        <input
                          {...register('primaryContact.name')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                        {errors.primaryContact?.name && (
                          <p className="mt-2 text-sm text-error-600">{errors.primaryContact.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                        <input
                          {...register('primaryContact.title')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                          placeholder="e.g., CFO, Accounts Manager"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email <span className="text-error-500">*</span>
                        </label>
                        <input
                          {...register('primaryContact.email')}
                          type="email"
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                        {errors.primaryContact?.email && (
                          <p className="mt-2 text-sm text-error-600">{errors.primaryContact.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone <span className="text-error-500">*</span>
                        </label>
                        <input
                          {...register('primaryContact.phone')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                        {errors.primaryContact?.phone && (
                          <p className="mt-2 text-sm text-error-600">{errors.primaryContact.phone.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Secondary Contact <span className="text-gray-500 font-normal text-lg">(Optional)</span></h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                        <input
                          {...register('secondaryContact.name')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                        <input
                          {...register('secondaryContact.title')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                        <input
                          {...register('secondaryContact.email')}
                          type="email"
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                        <input
                          {...register('secondaryContact.phone')}
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Financial Information */}
              {currentStep === 4 && (
                <div className="space-y-6 animate-fadeIn">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Financial Information</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tax ID/EIN <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('taxId')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="XX-XXXXXXX"
                      />
                      {errors.taxId && <p className="mt-2 text-sm text-error-600">{errors.taxId.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Payment Terms <span className="text-error-500">*</span>
                      </label>
                      <select
                        {...register('paymentTerms')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      >
                        <option value="">Select terms</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 60">Net 60</option>
                        <option value="Net 90">Net 90</option>
                        <option value="Due on Receipt">Due on Receipt</option>
                      </select>
                      {errors.paymentTerms && <p className="mt-2 text-sm text-error-600">{errors.paymentTerms.message}</p>}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800 mt-8 mb-4">Bank Information</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Bank Name <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('bankInfo.bankName')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      />
                      {errors.bankInfo?.bankName && (
                        <p className="mt-2 text-sm text-error-600">{errors.bankInfo.bankName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Account Holder Name <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('bankInfo.accountHolderName')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      />
                      {errors.bankInfo?.accountHolderName && (
                        <p className="mt-2 text-sm text-error-600">{errors.bankInfo.accountHolderName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Account Number <span className="text-error-500">*</span>
                      </label>
                      <input
                        {...register('bankInfo.accountNumber')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      />
                      {errors.bankInfo?.accountNumber && (
                        <p className="mt-2 text-sm text-error-600">{errors.bankInfo.accountNumber.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Routing Number</label>
                      <input
                        {...register('bankInfo.routingNumber')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Business Details */}
              {currentStep === 5 && (
                <div className="space-y-6 animate-fadeIn">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Business Details</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Type <span className="text-error-500">*</span>
                      </label>
                      <select
                        {...register('businessType')}
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      >
                        <option value="">Select type</option>
                        <option value="Corporation">Corporation</option>
                        <option value="LLC">LLC</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Sole Proprietorship">Sole Proprietorship</option>
                      </select>
                      {errors.businessType && <p className="mt-2 text-sm text-error-600">{errors.businessType.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Years in Business</label>
                      <input
                        {...register('yearsInBusiness', { valueAsNumber: true })}
                        type="number"
                        className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (Optional)</label>
                    <textarea
                      {...register('notes')}
                      rows={4}
                      className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      placeholder="Any additional information you'd like to provide"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Special Requirements (Optional)</label>
                    <textarea
                      {...register('specialRequirements')}
                      rows={4}
                      className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                      placeholder="Any special requirements or considerations"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-8 flex justify-between pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  className="inline-flex items-center px-6 py-3.5 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>

                {currentStep < steps.length ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="inline-flex items-center px-6 py-3.5 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-glow-lg transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                  >
                    Next
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={isSubmitting}
                    className="inline-flex items-center px-8 py-3.5 bg-gradient-success text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Registration
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Back to Home Link */}
          <div className="mt-6 text-center animate-fadeIn">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors group"
            >
              <svg className="w-4 h-4 mr-1.5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Blob Animation CSS */}
      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(20px, -50px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          75% {
            transform: translate(50px, 50px) scale(1.05);
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default CustomerRegistrationPageModern;
